// بازدیدِ مشترک: اتصال به «سرورِ بازدید» (/live/ws)، آدمک‌های بقیه، درهای مشترک و صدا.
//
// • جای هر نفر چند بار در ثانیه فرستاده می‌شود؛ آدمک‌ها روی گوشیِ هر کس نرم دنبالش می‌کنند (liveAvatar.jsx)
// • صدا: مستقیم بینِ گوشی‌ها (WebRTC)، هر نفر با هر نفر (تا ۴ نفر). تازه‌وارد به همه پیشنهادِ اتصال می‌دهد، بقیه
//   جواب می‌دهند — پس هیچ‌وقت دو طرف هم‌زمان پیشنهاد نمی‌دهند. اگر اتصالِ مستقیم نشد، از سرورِ واسط (TURN) رد می‌شود.
// • صدای هر نفر از سمتِ آدمکش می‌آید و با دور شدن آرام‌تر می‌شود (Web Audio)
// • میکروفون مثلِ تماسِ تلفنی همیشه روشن است؛ دکمه‌ی قطع/وصل دارد
//
// استفاده: const live = new LiveSession({ room, name, char, design }); live.start() — باید در همان لمسِ کاربر
// صدا زده شود (مرورگرها پخشِ صدا را فقط بعد از لمس اجازه می‌دهند). live.close() در پایان.

const POS_INTERVAL_MS = 100; // ۱۰ بار در ثانیه
const POS_KEEPALIVE_MS = 1500;
const SPEAK_ON = 0.035; // آستانه‌ی «در حالِ صحبت» (RMS)
const SPEAK_OFF = 0.02;
const NEAR_CM = 200; // تا این فاصله صدا کامل
const FAR_CM = 1400; // از این فاصله به بعد کمترین صدا
const MIN_GAIN = 0.3;

const wsUrl = () => `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/live/ws`;
// اگر WebSocket تا این مدت به «خوش‌آمد» نرسید، همان پیام‌ها با درخواست‌های معمولیِ HTTPS (بعضی اینترنت‌ها — مخصوصاً
// اینترنتِ همراه — WebSocket را از Cloudflare رد نمی‌کنند)
const WS_WAIT_MS = 6000;
const HTTP_FLUSH_MS = 120;

export class LiveSession {
  constructor({ room, name, char, design }) {
    this.room = room;
    this.name = name;
    this.char = char;
    this.design = design;
    this.selfId = null;
    this.peers = new Map(); // id → { id, name, char, muted, speaking, pos: {x,z,yaw,m,t} }
    this.doors = new Set();
    this.listeners = new Set();
    this.doorListeners = new Set();
    this.eventListeners = new Set();
    this.status = 'connecting'; // connecting | live | full | error | closed
    this.muted = false;
    this.speaking = false;
    this.micState = 'pending'; // pending | on | denied
    this.ws = null;
    this.mode = 'ws'; // ws | http
    this.wsFailures = 0;
    this.http = null; // { sid, outbox, flushTimer, gen }
    this.closed = false;
    this.retry = 0;
    this.lastPosSent = 0;
    this.lastPos = null;
    this.ice = [];
    this.pcs = new Map(); // peerId → { pc, initiator, audio, nodes }
    this.localStream = null;
    this.ctx = null;
  }

  // ---------- چرخه ----------
  start() {
    // AudioContext همین حالا (داخلِ لمسِ کاربر) ساخته و فعال می‌شود
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        this.ctx = new AC();
        if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
      }
    } catch (e) {
      this.ctx = null;
    }
    this.micPromise = this.openMic();
    this.connect();
    this.onVisible = () => {
      if (document.visibilityState === 'visible' && this.ctx && this.ctx.state !== 'running') this.ctx.resume().catch(() => {});
    };
    document.addEventListener('visibilitychange', this.onVisible);
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.setStatus('closed');
    clearTimeout(this.retryTimer);
    clearTimeout(this.wsWaitTimer);
    clearInterval(this.speakTimer);
    this.stopHttp(true);
    document.removeEventListener('visibilitychange', this.onVisible);
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {
        /* */
      }
    }
    this.pcs.forEach((_, id) => this.dropPeerConnection(id));
    if (this.localStream) this.localStream.getTracks().forEach((t) => t.stop());
    if (this.ctx) this.ctx.close().catch(() => {});
    this.listeners.clear();
    this.doorListeners.clear();
    this.eventListeners.clear();
  }

  // ---------- اشتراک برای React ----------
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  onDoor(fn) {
    this.doorListeners.add(fn);
    return () => this.doorListeners.delete(fn);
  }
  // رویدادهای نمایشی: { type: 'join' | 'leave' | 'full' | 'mic-denied' | 'reconnect', name }
  onEvent(fn) {
    this.eventListeners.add(fn);
    return () => this.eventListeners.delete(fn);
  }
  snapshot() {
    return {
      status: this.status,
      selfId: this.selfId,
      muted: this.muted,
      speaking: this.speaking,
      micState: this.micState,
      peers: [...this.peers.values()].map(({ id, name, char, muted, speaking }) => ({ id, name, char, muted, speaking })),
    };
  }
  emit() {
    const snap = this.snapshot();
    this.listeners.forEach((fn) => fn(snap));
  }
  event(e) {
    this.eventListeners.forEach((fn) => fn(e));
  }
  setStatus(s) {
    this.status = s;
    this.emit();
  }

  // ---------- اتصال ----------
  connect() {
    if (this.closed) return;
    if (this.mode === 'http') {
      this.connectHttp();
      return;
    }
    let ws;
    try {
      ws = new WebSocket(wsUrl());
    } catch (e) {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    // اگر به «خوش‌آمد» نرسید → راهِ دوم (HTTP)
    clearTimeout(this.wsWaitTimer);
    this.wsWaitTimer = setTimeout(() => {
      if (this.ws === ws && this.status !== 'live') this.switchToHttp();
    }, WS_WAIT_MS);
    ws.onopen = () => {
      this.send({ t: 'join', room: this.room, name: this.name, char: this.char, design: this.design, muted: this.muted });
    };
    ws.onmessage = (e) => {
      let m;
      try {
        m = JSON.parse(e.data);
      } catch (err) {
        return;
      }
      this.handle(m);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      // آدمک‌ها و صداها پاک؛ با اتصالِ دوباره از نو
      this.pcs.forEach((_, id) => this.dropPeerConnection(id));
      this.peers.clear();
      this.selfId = null;
      if (this.closed || this.status === 'full') return;
      // پیش از رسیدن به «خوش‌آمد» دو بار قطع شد → راهِ دوم
      if (this.status !== 'live') this.wsFailures += 1;
      if (this.wsFailures >= 2) {
        this.switchToHttp();
        return;
      }
      this.setStatus('connecting');
      this.scheduleReconnect();
    };
    ws.onerror = () => {};
  }

  scheduleReconnect() {
    if (this.closed) return;
    this.retry += 1;
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => this.connect(), Math.min(15000, 800 * 2 ** Math.min(this.retry, 5)));
  }

  send(m) {
    if (this.mode === 'http') {
      if (!this.http || !this.http.sid) return;
      // جای آدمک: فقط آخرینش (صف پر نشود)
      if (m.t === 'pos') this.http.outbox = this.http.outbox.filter((x) => x.t !== 'pos');
      this.http.outbox.push(m);
      if (!this.http.flushTimer) this.http.flushTimer = setTimeout(() => this.flushHttp(), HTTP_FLUSH_MS);
      return;
    }
    if (this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify(m));
  }

  // ---------- راهِ دوم: HTTP ----------
  switchToHttp() {
    if (this.closed || this.mode === 'http') return;
    this.mode = 'http';
    clearTimeout(this.wsWaitTimer);
    clearTimeout(this.retryTimer);
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      try {
        ws.close();
      } catch (e) {
        /* */
      }
    }
    this.connectHttp();
  }

  async post(path, body) {
    const r = await fetch(`/live/http/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      cache: 'no-store',
    });
    if (r.status === 410) {
      const err = new Error('gone');
      err.gone = true;
      throw err;
    }
    if (!r.ok) throw new Error(`http-${r.status}`);
    return r.json();
  }

  stopHttp(sendClose) {
    const h = this.http;
    if (!h) return;
    this.http = null;
    clearTimeout(h.flushTimer);
    if (sendClose && h.sid) this.post('close', { sid: h.sid }).catch(() => {});
  }

  async connectHttp() {
    if (this.closed) return;
    this.stopHttp(false);
    const h = { sid: null, outbox: [], flushTimer: null };
    this.http = h;
    try {
      const r = await this.post('open', {});
      if (this.http !== h) return;
      h.sid = r.sid;
      h.outbox.push({ t: 'join', room: this.room, name: this.name, char: this.char, design: this.design, muted: this.muted });
      await this.flushHttp();
      this.pollHttp(h);
    } catch (e) {
      if (this.http === h) this.httpLost(h);
    }
  }

  async flushHttp() {
    const h = this.http;
    if (!h || !h.sid) return;
    clearTimeout(h.flushTimer);
    h.flushTimer = null;
    if (!h.outbox.length) return;
    const msgs = h.outbox.splice(0, 60);
    try {
      await this.post('send', { sid: h.sid, msgs });
    } catch (e) {
      if (this.http === h) this.httpLost(h);
      return;
    }
    if (h.outbox.length && this.http === h) h.flushTimer = setTimeout(() => this.flushHttp(), HTTP_FLUSH_MS);
  }

  async pollHttp(h) {
    while (this.http === h && !this.closed) {
      let r;
      try {
        r = await this.post('recv', { sid: h.sid });
      } catch (e) {
        if (this.http === h) this.httpLost(h);
        return;
      }
      if (this.http !== h) return;
      (r.msgs || []).forEach((m) => this.handle(m));
      if (r.closed) {
        if (this.http === h) this.httpLost(h);
        return;
      }
    }
  }

  // اتصالِ HTTP قطع شد: مثلِ قطعِ WebSocket — آدمک‌ها و صداها پاک، و دوباره
  httpLost(h) {
    if (this.http !== h) return;
    this.http = null;
    clearTimeout(h.flushTimer);
    this.pcs.forEach((_, id) => this.dropPeerConnection(id));
    this.peers.clear();
    this.selfId = null;
    if (this.closed || this.status === 'full') return;
    this.setStatus('connecting');
    this.scheduleReconnect();
  }

  handle(m) {
    switch (m.t) {
      case 'welcome': {
        clearTimeout(this.wsWaitTimer);
        const wasReconnect = this.retry > 0;
        this.retry = 0;
        this.selfId = m.id;
        this.ice = Array.isArray(m.ice) ? m.ice : [];
        this.peers.clear();
        (m.peers || []).forEach((p) => this.peers.set(p.id, { ...p, pos: p.pos ? { ...p.pos, t: performance.now() } : null }));
        const doors = new Set((m.doors || []).map(String));
        this.doors = doors;
        this.doorListeners.forEach((fn) => fn({ all: doors }));
        this.setStatus('live');
        if (wasReconnect) this.event({ type: 'reconnect' });
        if (this.lastPos) this.sendPos(this.lastPos.x, this.lastPos.z, this.lastPos.yaw, this.lastPos.m, true);
        if (this.muted || this.speaking) this.send({ t: 'state', muted: this.muted, speaking: this.speaking });
        // تازه‌وارد به همه پیشنهادِ اتصالِ صدا می‌دهد
        this.peers.forEach((p) => this.ensurePeerConnection(p.id, true));
        break;
      }
      case 'full':
        this.setStatus('full');
        this.event({ type: 'full', max: m.max });
        break;
      case 'error':
        this.setStatus('error');
        break;
      case 'peer-join':
        if (!m.peer) return;
        this.peers.set(m.peer.id, { ...m.peer, pos: null });
        this.event({ type: 'join', name: m.peer.name });
        this.emit();
        break;
      case 'peer-leave': {
        const p = this.peers.get(m.id);
        this.peers.delete(m.id);
        this.dropPeerConnection(m.id);
        if (p) this.event({ type: 'leave', name: p.name });
        this.emit();
        break;
      }
      case 'pos': {
        const p = this.peers.get(m.id);
        if (p) p.pos = { x: m.x, z: m.z, yaw: m.yaw, m: m.m, t: performance.now() };
        break;
      }
      case 'door':
        if (m.open) this.doors.add(String(m.id));
        else this.doors.delete(String(m.id));
        this.doorListeners.forEach((fn) => fn({ id: m.id, open: m.open, by: m.by }));
        break;
      case 'state': {
        const p = this.peers.get(m.id);
        if (p) {
          p.muted = Boolean(m.muted);
          p.speaking = Boolean(m.speaking);
          this.emit();
        }
        break;
      }
      case 'signal':
        this.onSignal(m.from, m.data);
        break;
      default:
    }
  }

  // ---------- حرکت و درها ----------
  sendPos(x, z, yaw, moving, force = false) {
    const now = performance.now();
    const prev = this.lastPos;
    const changed = !prev || Math.abs(prev.x - x) > 0.5 || Math.abs(prev.z - z) > 0.5 || Math.abs(prev.yaw - yaw) > 0.01 || prev.m !== moving;
    if (!force && now - this.lastPosSent < (changed ? POS_INTERVAL_MS : POS_KEEPALIVE_MS)) return;
    this.lastPos = { x, z, yaw, m: moving ? 1 : 0 };
    this.lastPosSent = now;
    this.send({ t: 'pos', x: Math.round(x * 10) / 10, z: Math.round(z * 10) / 10, yaw: Math.round(yaw * 1000) / 1000, m: moving ? 1 : 0 });
  }

  sendDoor(id, open) {
    if (open) this.doors.add(String(id));
    else this.doors.delete(String(id));
    this.send({ t: 'door', id, open });
  }

  // ---------- میکروفون ----------
  async openMic() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.micState = 'denied';
      this.emit();
      this.event({ type: 'mic-denied' });
      return null;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      if (this.closed) {
        stream.getTracks().forEach((t) => t.stop());
        return null;
      }
      this.localStream = stream;
      stream.getAudioTracks().forEach((t) => {
        t.enabled = !this.muted;
      });
      this.micState = 'on';
      this.watchSpeaking(stream);
      // اتصال‌هایی که قبل از اجازه‌ی میکروفون ساخته شدند: صدای خودمان را اضافه کن
      this.pcs.forEach((entry) => this.attachLocal(entry));
      this.emit();
      return stream;
    } catch (e) {
      this.micState = 'denied';
      this.emit();
      this.event({ type: 'mic-denied' });
      return null;
    }
  }

  setMuted(muted) {
    this.muted = Boolean(muted);
    if (this.localStream) this.localStream.getAudioTracks().forEach((t) => (t.enabled = !this.muted));
    if (this.muted) this.speaking = false;
    this.send({ t: 'state', muted: this.muted, speaking: this.speaking });
    this.emit();
  }

  watchSpeaking(stream) {
    if (!this.ctx) return;
    try {
      const src = this.ctx.createMediaStreamSource(stream);
      const an = this.ctx.createAnalyser();
      an.fftSize = 512;
      src.connect(an);
      const buf = new Float32Array(an.fftSize);
      this.speakTimer = setInterval(() => {
        if (this.muted) return;
        an.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        const next = this.speaking ? rms > SPEAK_OFF : rms > SPEAK_ON;
        if (next !== this.speaking) {
          this.speaking = next;
          this.send({ t: 'state', muted: this.muted, speaking: next });
          this.emit();
        }
      }, 150);
    } catch (e) {
      /* بدونِ نشانگرِ صحبت */
    }
  }

  // ---------- صدا (WebRTC) ----------
  ensurePeerConnection(peerId, initiator) {
    if (this.pcs.has(peerId) || typeof RTCPeerConnection === 'undefined') return this.pcs.get(peerId);
    const pc = new RTCPeerConnection({ iceServers: this.ice });
    const entry = { pc, initiator, audio: null, nodes: null, sender: null, makingOffer: false };
    this.pcs.set(peerId, entry);
    // صدای خودمان: با addTrack (تا در جواب دادن هم همان مسیر استفاده شود). اگر میکروفون هنوز آماده نیست، پیشنهاددهنده
    // یک مسیرِ خالیِ دوطرفه می‌سازد و جواب‌دهنده بعد از رسیدنِ پیشنهاد (پایین‌تر در onSignal)؛ صدا بعداً با
    // replaceTrack رویش می‌نشیند — بدونِ مذاکره‌ی دوباره
    if (this.localStream) this.attachLocal(entry);
    else if (initiator) entry.transceiver = pc.addTransceiver('audio', { direction: 'sendrecv' });

    pc.onicecandidate = (e) => {
      if (e.candidate) this.send({ t: 'signal', to: peerId, data: { candidate: e.candidate.toJSON ? e.candidate.toJSON() : e.candidate } });
    };
    pc.ontrack = (e) => this.playRemote(peerId, e.streams[0] || new MediaStream([e.track]));
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' && entry.initiator) {
        // اتصالِ مستقیم قطع شد — دوباره، این بار شاید از سرورِ واسط
        try {
          pc.restartIce();
        } catch (err) {
          /* */
        }
        this.makeOffer(peerId, entry, true);
      }
    };
    if (initiator) this.makeOffer(peerId, entry);
    return entry;
  }

  attachLocal(entry) {
    if (!this.localStream || entry.sender) return;
    const track = this.localStream.getAudioTracks()[0];
    if (!track) return;
    if (entry.transceiver) {
      entry.transceiver.sender.replaceTrack(track).catch(() => {});
      entry.sender = entry.transceiver.sender;
    } else {
      entry.sender = entry.pc.addTrack(track, this.localStream);
    }
  }

  async makeOffer(peerId, entry, iceRestart = false) {
    if (entry.makingOffer) return;
    entry.makingOffer = true;
    try {
      // اگر میکروفون هنوز در حالِ اجازه گرفتن است، کمی صبر (حداکثر ۴ ثانیه) تا صدا از اول در اتصال باشد
      if (this.micState === 'pending' && this.micPromise) {
        await Promise.race([this.micPromise, new Promise((r) => setTimeout(r, 4000))]);
      }
      const offer = await entry.pc.createOffer(iceRestart ? { iceRestart: true } : undefined);
      await entry.pc.setLocalDescription(offer);
      this.send({ t: 'signal', to: peerId, data: { sdp: entry.pc.localDescription } });
    } catch (e) {
      /* */
    } finally {
      entry.makingOffer = false;
    }
  }

  async onSignal(from, data) {
    if (!data || !this.peers.has(from)) return;
    const entry = this.ensurePeerConnection(from, false);
    if (!entry) return;
    const { pc } = entry;
    try {
      if (data.sdp) {
        await pc.setRemoteDescription(data.sdp);
        if (data.sdp.type === 'offer') {
          if (!entry.sender && !entry.transceiver) {
            const tr = pc.getTransceivers().find((x) => x.receiver && x.receiver.track && x.receiver.track.kind === 'audio');
            if (tr) {
              tr.direction = 'sendrecv';
              entry.transceiver = tr;
              this.attachLocal(entry);
            }
          }
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          this.send({ t: 'signal', to: from, data: { sdp: pc.localDescription } });
        }
      } else if (data.candidate) {
        await pc.addIceCandidate(data.candidate).catch(() => {});
      }
    } catch (e) {
      /* */
    }
  }

  playRemote(peerId, stream) {
    const entry = this.pcs.get(peerId);
    if (!entry) return;
    if (!entry.audio) {
      const a = document.createElement('audio');
      a.autoplay = true;
      a.playsInline = true;
      a.setAttribute('playsinline', '');
      a.style.display = 'none';
      document.body.appendChild(a);
      entry.audio = a;
    }
    entry.audio.srcObject = stream;
    // صدای فضایی: جریان از Web Audio پخش می‌شود و عنصرِ audio بی‌صدا فقط جریان را «زنده» نگه می‌دارد (کرومِ اندروید)
    if (this.ctx && !entry.nodes) {
      try {
        const src = this.ctx.createMediaStreamSource(stream);
        const gain = this.ctx.createGain();
        const pan = this.ctx.createStereoPanner ? this.ctx.createStereoPanner() : null;
        src.connect(gain);
        if (pan) {
          gain.connect(pan);
          pan.connect(this.ctx.destination);
        } else gain.connect(this.ctx.destination);
        entry.nodes = { src, gain, pan };
        entry.audio.muted = true;
      } catch (e) {
        entry.nodes = null;
        entry.audio.muted = false;
      }
    } else if (!this.ctx) entry.audio.muted = false;
    entry.audio.play().catch(() => {});
  }

  dropPeerConnection(peerId) {
    const entry = this.pcs.get(peerId);
    if (!entry) return;
    this.pcs.delete(peerId);
    try {
      entry.pc.close();
    } catch (e) {
      /* */
    }
    if (entry.nodes) {
      try {
        entry.nodes.src.disconnect();
        entry.nodes.gain.disconnect();
        if (entry.nodes.pan) entry.nodes.pan.disconnect();
      } catch (e) {
        /* */
      }
    }
    if (entry.audio) {
      entry.audio.srcObject = null;
      entry.audio.remove();
    }
  }

  // هر فریم (از صحنه): صدای هر نفر بر اساسِ فاصله و جهتش نسبت به ما (سانتی‌متر، yaw با همان قراردادِ دوربین)
  updateListener(x, z, yaw) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.pcs.forEach((entry, id) => {
      const p = this.peers.get(id);
      if (!entry.nodes || !p || !p.pos) return;
      const dx = p.pos.x - x;
      const dz = p.pos.z - z;
      const dist = Math.hypot(dx, dz);
      const k = Math.min(1, Math.max(0, (dist - NEAR_CM) / (FAR_CM - NEAR_CM)));
      entry.nodes.gain.gain.setTargetAtTime(1 - k * (1 - MIN_GAIN), t, 0.15);
      if (entry.nodes.pan) {
        // راستِ ما = (cos yaw, -sin yaw)؛ جلو = (-sin yaw, -cos yaw)
        const right = (dx * Math.cos(yaw) - dz * Math.sin(yaw)) / Math.max(1, dist);
        entry.nodes.pan.pan.setTargetAtTime(Math.max(-0.8, Math.min(0.8, right * 0.8)), t, 0.15);
      }
    });
  }
}
